import https from 'https';
import querystring from 'querystring';
import { firestore } from './firebase'
const queryify = querystring.stringify;

const exported = {};

/**
 * The Stripe secret key used to live in this file. It now comes from the
 * build environment so it is not committed to the repository.
 */
const stripeSecretKey = process.env.VUE_APP_STRIPE_SECRET_KEY;

if (!stripeSecretKey) {
  console.warn('VUE_APP_STRIPE_SECRET_KEY is not set, Stripe calls will fail.')
}

/**
 * A Stripe request that rejects instead of leaving the promise hanging.
 */
function stripeRequest(options, onResponse) {
  return new Promise((resolve, reject) => {
    const req = https.request(options, (res) => {
      const chunks = [];
      res.on('data', (chunk) => chunks.push(chunk));
      res.on('end', () => {
        const body = Buffer.concat(chunks).toString();
        let result;
        try {
          result = JSON.parse(body);
        } catch (e) {
          reject(new Error('Stripe returned an unreadable response'));
          return;
        }
        onResponse(result, resolve, reject);
      });
    });
    req.on('error', (error) => reject(error));
    req.end();
  });
}

const baseRequest = () => ({
  method: 'POST',
  hostname: 'api.stripe.com',
  port: null,
  headers: {
    'authorization': `Bearer ${stripeSecretKey}`,
    'content-length': '11',
  },
})

/**
 * @description Reads the shared app config from Firestore.
 */
async function getConfig() {
  const snapshot = await firestore.collection('config').doc('app').get();
  return snapshot.exists ? snapshot.data() : {};
}

exported.newCustomer = (parent) => {
  return new Promise((resolve, reject) => {
    if (parent.get('stripeId')) {
      resolve(true)
      return
    }
    const email = customerEmail(parent);
    if (!email) {
      reject(new Error('No email address for the family account'))
      return
    }
    const options = Object.assign(baseRequest(), {
      path: '/v1/customers?' + queryify({ email }),
    });
    stripeRequest(options, async (result, done, fail) => {
      if (result.error) {
        fail(new Error(result.error.message))
        return
      }
      try {
        parent.set('stripeId', result.id);
        // The customer id has to reach Firestore before the call is
        // reported as done, otherwise it is lost when the page closes.
        await parent.save();
        // The copy of the record the app holds has to know about the
        // customer too, otherwise the billing screens keep sending an
        // empty customer id.
        const current = activeParent();
        if (current && current.id === parent.id && !current.get('stripeId')) {
          current.set('stripeId', result.id);
        }
      } catch (error) {
        fail(error);
        return;
      }
      done(true)
    }).then(resolve).catch(reject)
  })
}

/**
 * The parent record the app is holding.
 */
function activeParent() {
  return VueInstance && VueInstance.$data ? VueInstance.$data.parent : null;
}

/**
 * The Stripe customer for the family account.
 *
 * The customer id lives on the parent record, so the record is passed in
 * instead of read from a global copy that can be out of date.
 */
function customerId(parent) {
  const stripeId = parent ? parent.get('stripeId') : null;
  if (!stripeId) {
    throw new Error('This family account has no Stripe customer yet');
  }
  return stripeId;
}

/**
 * The email of the family account that owns the parent record.
 */
function customerEmail(parent) {
  const profile = parent.get('user');
  return profile ? profile.get('email') : null;
}

exported.charge = (parent, reward) => {
  return new Promise((resolve, reject) => {
    // The work runs inside the promise so a failure anywhere below ends up
    // as a rejection instead of a promise that never settles.
    (async () => {
      try {
        const email = customerEmail(parent);
        if (!email) {
          throw new Error('No signed in family account')
        }
        const customer = customerId(parent);

        const config = await getConfig();
        const defaultTax = config.defaultTax || 0.07;

        let fee = reward.get('cost') * (reward.get('collectionRate') || defaultTax);
        let options = Object.assign(baseRequest(), {
          path: '/v1/charges?' + queryify({
            //'transfer_data[amount]': fee < 1 ? 100 : fee * 100,
            amount: reward.get('cost') * 100,
            description: 'Chore Champion Reward',
            statement_descriptor: 'Reward',
            //  'transfer_data[destination]': reward.get('business').get('stripeId'),
            'receipt_email': email,
            'currency': 'usd',
            'customer': customer
          }),
        });

        const result = await stripeRequest(options, (response, done, fail) => {
          if (response?.error?.code == 'missing') {
            fail(new Error('The payment method is missing'))
            return
          }
          else if (response.outcome?.network_status == 'declined_by_network') {
            fail(new Error('Transaction Declined'))
            return
          } else if (response.paid && response.status == 'succeeded') {
            done(response)
            return
          } else {
            fail(new Error('The payment did not go through'))
          }
        });

        gtag('event', 'purchase', {
          "transaction_id": result.id,
          "value": result.amount / 100,
          "currency": result.currency,
          "shipping": 0,
          "items": [
            {
              "id": reward._getId(),
              "name": reward.get('name'),
              "brand": reward.get('businessName'),
              "quantity": 1,
              "price": result.amount / 100
            }
          ]
        });

        try {
          await recordOrder(reward, parent, result)
        } catch (error) {
          // The card was already charged, so this cannot be swallowed.
          throw new Error('The payment went through but the order could not be saved. Please contact support with this purchase.')
        }
        resolve(result)
      } catch (error) {
        reject(error)
      }
    })();
  })
}

async function recordOrder(reward, parent, result) {
  const order = firestore.collection('orders').doc();
  await order.set({
    rewardId: reward._getId(),
    rewardName: reward.get('name'),
    userId: parent.get('userId') || parent.id,
    businessName: reward.get('businessName') || null,
    amount: result.amount / 100,
    currency: result.currency,
    createdAt: Date.now(),
  })
  return result
}
/**
 * @description Tokenizes cards
 * @return {object}
 * @param {object} card
 */
exported.tokenizeCard = (card) => {
  return new Promise((resolve, reject) => {
    const options = Object.assign(baseRequest(), {
      path: '/v1/tokens?' + queryify({
        'card[number]': card.number.replace(' ', ''),
        'card[exp_month]': Number(card.exp.split('/')[0]),
        'card[exp_year]': Number(card.exp.split('/')[1]),
        'card[cvc]': card.cvc
      }),
    });
    stripeRequest(options, (result, done, fail) => {
      if (result.error) fail(result.error)
      else done(result.id)
    }).then(resolve).catch(reject)
  })
}

exported.addCardToAccount = (parent, token) => {
  return new Promise((resolve, reject) => {
    let stripeId;
    try {
      stripeId = customerId(parent);
    } catch (error) {
      reject(error);
      return;
    }
    const options = Object.assign(baseRequest(), {
      path: `/v1/customers/${stripeId}?` + queryify({ source: token })
    })
    stripeRequest(options, (result, done, fail) => {
      (result.error !== undefined) ? fail(result.error) : done(result)
    }).then(resolve).catch(reject)
  });
}

exported.getAllCards = (parent) => {
  return new Promise((resolve, reject) => {
    let stripeId;
    try {
      stripeId = customerId(parent);
    } catch (error) {
      reject(error);
      return;
    }
    const options = {
      ...baseRequest(), ...{
        method: 'GET',
        path: `/v1/customers/${stripeId}/sources?` + queryify({ object: 'card' })
      }
    }
    stripeRequest(options, (result, done, fail) => {
      if (result.error !== undefined) fail(result.error)
      else done(result)
    }).then(resolve).catch(reject)
  });
}

export default exported;
